import { useEffect, useRef } from "react";

import Icon from "common/core/icon";
import { isiOSDevice } from "util/support";
import { useFeatureFlag } from "common/feature_gating";

import type { LocalParty, RemoteParty } from ".";
import Styles from "./audio_status.module.scss";

type Props = {
  party: LocalParty | RemoteParty;
  isLocalParty?: boolean;
};

// copied from https://stackoverflow.com/questions/5294955/how-to-scale-down-a-range-of-numbers-with-a-known-min-and-max-value
function scaleBetween(
  unscaledNum: number,
  minAllowed: number,
  maxAllowed: number,
  min: number,
  max: number,
) {
  return Math.ceil(((maxAllowed - minAllowed) * (unscaledNum - min)) / (max - min) + minAllowed);
}

function ParticipantAudioStatus({ party, isLocalParty = false }: Props) {
  const volumeRef = useRef<HTMLDivElement>(null);
  const audioStatusDisabled = !useFeatureFlag("mweb-audio-status-indicator") || isiOSDevice();

  useEffect(() => {
    if (!party.micEnabled || !party.volumeChanged$ || audioStatusDisabled) {
      return;
    }
    if (volumeRef.current?.style) {
      volumeRef.current.parentElement?.classList.add(Styles.active);
    }
    const volumeSubscription = party.volumeChanged$.subscribe((volume) => {
      if (volumeRef.current?.style) {
        // our min px height is 4px, with the max being 16
        // so we need to scale our 0-100 volume percentage to be between 25% and 100%
        // else any volume less than 25% shows no movement

        const scaled = scaleBetween(volume, 25, 110, 5, 100);
        if (scaled <= 25) {
          volumeRef.current.parentElement?.classList.remove(Styles.active);
        } else {
          volumeRef.current.parentElement?.classList.add(Styles.active);
        }
        volumeRef.current.style.height = `${scaled}%`;
      }
    });
    return () => {
      volumeSubscription.unsubscribe();
    };
  }, [party.volumeChanged$, party.micEnabled]);

  let micLabel;
  if (!party.micEnabled) {
    micLabel = isLocalParty ? "You are muted" : `${party.participants[0]?.fullName} is muted`;
  } else {
    micLabel = isLocalParty
      ? "You are not muted"
      : `${party.participants[0]?.fullName} is not muted`;
  }

  return (
    <span className={Styles.audioStatus}>
      {!party.micEnabled || audioStatusDisabled ? (
        <Icon
          aria-label={micLabel}
          className={party.micEnabled ? Styles.unmuted : Styles.muted}
          name={party.micEnabled ? "microphone" : "microphone-off"}
          size="large"
        />
      ) : (
        <div className={Styles.volumeContainer}>
          <div className={Styles.volume} ref={volumeRef}>
            <div className={Styles.volumeBar}></div>
            <div className={Styles.volumeBar}></div>
            <div className={Styles.volumeBar}></div>
          </div>
        </div>
      )}
    </span>
  );
}

export default ParticipantAudioStatus;
